import {UseQueryResult} from '@tanstack/react-query';
import {z} from 'zod';

import {TableSorterHookReturnType} from '../../../util/antd/table/table-sorter-hook';
import {PaginationType} from '../../../util/pagination-hook/pagination-hook-type';
import {PaginatedResponseType} from '../../api/api-type';
import {sentimentDataSchema} from '../review-sentiment/review-sentiment-type';

export const reviewAspectRecordSchema = z.object({
    ids: z.array(z.number()),
    name: z.string(),
    negativeReviews: z.object({
        value: z.number(),
        comparedValue: z.number().nullish(),
        delta: z.number().nullish(),
    }),
    neutralReviews: z.object({
        value: z.number(),
        comparedValue: z.number().nullish(),
        delta: z.number().nullish(),
    }),
    positiveReviews: z.object({
        value: z.number(),
        comparedValue: z.number().nullish(),
        delta: z.number().nullish(),
    }),
});

export type ReviewAspectRecordType = z.infer<typeof reviewAspectRecordSchema>;

export enum ReviewAspectTableSortedColumnsEnum {
    mainNegativeReviews = 'negative_reviews',
    deltaNegativeReviews = 'negative_reviews__delta',
    compareNegativeReviews = 'negative_reviews__compared_value',

    mainNeutralReviews = 'neutral_reviews',
    deltaNeutralReviews = 'neutral_reviews__delta',
    compareNeutralReviews = 'neutral_reviews__compared_value',

    mainPositiveReviews = 'positive_reviews',
    deltaPositiveReviews = 'positive_reviews__delta',
    comparePositiveReviews = 'positive_reviews__compared_value',
}

export const reviewAspectsToneDynamicsSchema = z.object({
    labels: z.array(z.string()),
    aspectsData: z.array(
        z.object({
            aspect: z.string(),
            currentPeriodData: z.array(z.number()),
            comparePeriodData: z.array(z.number()).nullish(),
        })
    ),
});

export type ReviewAspectsToneType = z.infer<typeof reviewAspectsToneDynamicsSchema>;

export const reviewAspectsToneDistributionSchema = z.object({
    aspectsTonality: z.array(
        z.object({
            aspect: z.string(),
            currentPeriodData: z.object({
                positive: z.number(),
                neutral: z.number(),
                negative: z.number(),
            }),
            comparePeriodData: z
                .object({
                    positive: z.number(),
                    neutral: z.number(),
                    negative: z.number(),
                })
                .nullish(),
        })
    ),
});

export type ReviewAspectsToneDistributionType = z.infer<typeof reviewAspectsToneDistributionSchema>;

export const reviewsCountByAspectsSchema = z.object({
    reviewsByAspects: z.array(
        z.object({
            aspect: z.string(),
            currentPeriodData: z.object({
                reviewsCount: z.number(),
                tonalityIdx: z.number(),
            }),
            comparePeriodData: z
                .object({
                    reviewsCount: z.number(),
                    tonalityIdx: z.number(),
                })
                .nullish(),
        })
    ),
});

export type ReviewsCountByAspectsType = z.infer<typeof reviewsCountByAspectsSchema>;

export type AspectsAnalysisRequestParametersType = {
    startDate: string | null;
    endDate: string | null;
    comparedStartDate?: string | null;
    comparedEndDate?: string | null;
    sources: Array<number>;
};

export type AspectsAnalysisTableRequestParametersType = AspectsAnalysisRequestParametersType & {
    sortQueryValue: string | null;
};

export type ReviewAnalyticsAspectsHookType = UseQueryResult<PaginatedResponseType<ReviewAspectRecordType>> &
    PaginationType & {
        sorter: TableSorterHookReturnType;
    };

export const topAspectsItemSchema = z.object({
    aspect: z.string(),
    avgRating: z.number(),
    reviewsCount: z.number(),
    sentiment: sentimentDataSchema,
});

export type TopAspectsItemType = z.infer<typeof topAspectsItemSchema>;

export type TopAspectsQueryParametersType = {
    startDate: string | null;
    endDate: string | null;
};
